import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getEcho } from '../../services/echoService';
import httpService from '../../services/httpService';
import { useChatStore } from '../../store/store';
import ChatInput from './ChatInput';
import ChatMessage from './ChatMessage';

/**
 * ChatThread — message list + input for one 1-to-1 chat thread.
 *
 * Identified by clubGuid + listenerGuid. The backend creates the chat record
 * on first request if it doesn't exist yet.
 *
 * Pusher is used only as a signal — when it fires, the component re-fetches
 * the latest messages from the REST API. The API is the single source of
 * truth; no message data is read from the Pusher payload.
 *
 * Props:
 *   clubGuid     string  — podcast club GUID
 *   listenerGuid string  — listeners.guid of the listener in this thread
 *   participant  object  — { first_name, last_name, avatar_url } for the header
 *   userType     'podcaster' | 'listener'
 *   onBack       () => void  — optional back button (mobile)
 */
export default function ChatThread({ clubGuid, listenerGuid, userInfoGuid, participant, userType, onBack }) {
    const [chatGuid, setChatGuid] = useState(null);

    const messages = useChatStore((s) => s.messages);
    const isLoading = useChatStore((s) => s.isLoading);
    const hasMore = useChatStore((s) => s.hasMore);

    const listRef = useRef(null);
    const isFirstLoad = useRef(true);

    const baseUrl = `chat/club/${clubGuid}/listener/${listenerGuid}`;

    // -----------------------------------------------------------------------
    // Initial load + Echo subscription whenever the thread changes
    // -----------------------------------------------------------------------
    useEffect(() => {
        const { resetThread } = useChatStore.getState();
        resetThread();
        isFirstLoad.current = true;
        setChatGuid(null);

        // Load the first page — response also gives us chatGuid for Echo
        fetchLatest();

        return () => leaveChannel(chatGuid);
    }, [clubGuid, listenerGuid]);

    // Subscribe once chatGuid is known (set after the first fetch)
    useEffect(() => {
        if (!chatGuid) return;

        const echo = getEcho();
        if (!echo) return;

        echo
            .private(`podcast-club-chat.${chatGuid}`)
            .listen('.message.sent', () => {
                // Pusher is a signal only — re-fetch from the API
                fetchLatest();
            });

        return () => leaveChannel(chatGuid);
    }, [chatGuid]);

    const leaveChannel = (guid) => {
        if (!guid) return;
        const echo = getEcho();
        if (echo) echo.leave(`podcast-club-chat.${guid}`);
    };

    // -----------------------------------------------------------------------
    // Fetch the latest 30 messages (replaces current view, scrolls to bottom)
    // -----------------------------------------------------------------------
    const fetchLatest = useCallback(async () => {
        const { setLoading, setMessages, setHasMore, updateChatLatestMessage } = useChatStore.getState();
        setLoading(true);
        try {
            const res = await httpService.get(`${baseUrl}/messages?limit=30`);
            const data = res.data?.data ?? [];
            const more = res.data?.has_more ?? false;
            const receivedChatGuid = res.data?.chat_guid;

            if (receivedChatGuid) setChatGuid((prev) => prev ?? receivedChatGuid);

            setMessages(data);
            setHasMore(more);
            setTimeout(scrollToBottom, 50);

            // Keep sidebar preview in sync
            if (data.length > 0 && receivedChatGuid) {
                const last = data[data.length - 1];
                updateChatLatestMessage(receivedChatGuid, {
                    message: last.message,
                    created_at: last.created_at,
                    user_guid: last.user?.guid,
                });
            }
        } catch {
            // silent
        } finally {
            useChatStore.getState().setLoading(false);
            isFirstLoad.current = false;
        }
    }, [baseUrl]);

    // -----------------------------------------------------------------------
    // Load older messages on scroll-up (cursor-based)
    // -----------------------------------------------------------------------
    const loadOlder = useCallback(async () => {
        const { isLoading, oldestMessageId, setLoading, prependMessages, setHasMore } = useChatStore.getState();
        if (isLoading || !oldestMessageId) return;
        setLoading(true);
        try {
            const res = await httpService.get(`${baseUrl}/messages?limit=30&before_id=${oldestMessageId}`);
            const data = res.data?.data ?? [];
            prependMessages(data);
            setHasMore(res.data?.has_more ?? false);
        } catch {
            // silent
        } finally {
            useChatStore.getState().setLoading(false);
        }
    }, [baseUrl]);

    const handleScroll = () => {
        const { hasMore, isLoading } = useChatStore.getState();
        if (!listRef.current || !hasMore || isLoading) return;
        if (listRef.current.scrollTop < 80) {
            const prevScrollHeight = listRef.current.scrollHeight;
            loadOlder().then(() => {
                if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight - prevScrollHeight;
                }
            });
        }
    };

    const scrollToBottom = () => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    };

    // -----------------------------------------------------------------------
    // Send — POST then re-fetch so the API is always the source of truth
    // -----------------------------------------------------------------------
    const handleSend = async ({ message, tags }) => {
        await httpService.post(`${baseUrl}/messages`, {
            message,
            tags: tags.map(({ type, guid }) => ({ type, guid })),
        });
        // Re-fetch so the sent message comes from the same pipeline as all others
        fetchLatest();
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    const participantName = participant
        ? `${participant.first_name ?? ''} ${participant.last_name ?? ''}`.trim()
        : 'Chat';

    return (
        <div className="chat-thread">
            <div className="chat-thread__header">
                {onBack && (
                    <button className="chat-thread__back" onClick={onBack} aria-label="Back">
                        ←
                    </button>
                )}
                {participant?.avatar_url ? (
                    <img
                        className="chat-thread__avatar"
                        src={`${process.env.REACT_APP_API_URL}/storage/avatars/${userInfoGuid}.png`}
                        alt={participantName}
                    />
                ) : (
                    <div className="chat-thread__avatar chat-thread__avatar--fallback">
                        {(participant?.first_name ?? '?')[0].toUpperCase()}
                    </div>
                )}
                <div className="chat-thread__header-info">
                    <span className="chat-thread__name">{participantName}</span>
                    <span className="chat-thread__role">
                        {userType === 'podcaster' ? 'Listener' : 'Podcaster'}
                    </span>
                </div>
            </div>

            <div
                className="chat-thread__messages"
                ref={listRef}
                onScroll={handleScroll}
            >
                {isLoading && isFirstLoad.current && (
                    <div className="chat-thread__status">Loading messages…</div>
                )}

                {hasMore && !isFirstLoad.current && (
                    <div className="chat-thread__status">
                        {isLoading ? 'Loading…' : 'Scroll up for older messages'}
                    </div>
                )}

                {messages.length === 0 && !isLoading && (
                    <div className="chat-thread__empty">
                        No messages yet. Start the conversation!
                    </div>
                )}

                {messages.map((msg) => (
                    <ChatMessage
                        key={msg.guid ?? msg.id}
                        message={msg}
                        clubGuid={clubGuid}
                    />
                ))}
            </div>

            <ChatInput
                clubGuid={clubGuid}
                listenerGuid={listenerGuid}
                onSend={handleSend}
            />
        </div>
    );
}
